import { aiModelCapabilityLabel, aiModelInputModalityLabel } from "@/lib/ai/model-vocab";
import { UPLOAD_CATEGORY_KINDS, type UploadCategoryKind } from "@/lib/upload/kinds";
import { isReadableChatDocument } from "./attachment-formats";
import type { AiInputLimits } from "./input-limits";

export type ModelFeatureProfile = {
  kind?: string;
  inputModalities: string[];
  inputLimits?: AiInputLimits;
  capabilities: string[];
};

export const CHAT_AUDIO_MEDIA_TYPES = ["audio/wav", "audio/x-wav", "audio/mpeg", "audio/mp3"];

export function modelHasCapability(
  profile: ModelFeatureProfile,
  capability: string,
): boolean {
  return profile.capabilities.includes(capability);
}

export function modelSupportsModality(
  profile: ModelFeatureProfile,
  modality: string,
): boolean {
  if (modality === "text") return true;

  return profile.inputModalities.includes(modality);
}

export function modelAttachmentKinds(profile: ModelFeatureProfile): UploadCategoryKind[] {
  return UPLOAD_CATEGORY_KINDS.filter(kind => {
    const modality = kind === "voice" ? "audio" : kind;
    if (["image", "audio", "video"].includes(modality)) {
      return !(profile.kind === "image_gen" && modality !== "image") && modelSupportsModality(profile, modality);
    }
    // ZIP belongs to other attachments, but no chat archive reader is connected yet.
    return kind !== "zip" && modelSupportsModality(profile, "file");
  });
}

export function modelAcceptsAttachment(profile: ModelFeatureProfile, mediaType: string, filename?: string): boolean {
  const modality = mediaType.split("/")[0];
  if (profile.kind === "image_gen" && ["audio", "video"].includes(modality)) return false;
  if (modality === "audio" && !CHAT_AUDIO_MEDIA_TYPES.includes(mediaType)) return false;
  return ["image", "audio", "video"].includes(modality)
    ? modelSupportsModality(profile, modality)
    : modelSupportsModality(profile, "file") && isReadableChatDocument(mediaType, filename);
}

export const CHAT_CAPABILITY_SLOTS = [
  { capability: "tools" },
  { capability: "reasoning" },
  { capability: "websearch" },
] as const;

export function attachmentSlotLabel(modality: string): string {
  return aiModelInputModalityLabel(modality);
}

export function capabilitySlotLabel(capability: string): string {
  return aiModelCapabilityLabel(capability);
}
